import { useTranslation } from 'react-i18next';
import FormLanguageSelector from '../common/FormLanguageSelector';
import { InputGroup } from '../common/InputGroup';
import { useServiceCategorySelector } from '../../hooks/useServiceCategorySelector';
import { DropdownMultiTypeahead } from '../common/DropdownMultiTypeahead';
import { Button } from '../common/Button';
import { ServiceCategory } from '../../types/service';
import { City } from '../../types/profile';
import FullBlockingModal from '../common/FullBlockingModal';
import { useCitySelector } from '../../hooks/useCitySelector';
import { createServiceGroupBy } from '../../utils/serviceUtils';
interface FilterModalProps {
  className?: string;
  selectedLanguages: string[];
  selectedServices: ServiceCategory[];
  selectedCities: City[];
  isBlocking?: boolean;
  onClose: () => void;
  onSubmit: () => void;
  setSelectedLanguages: (languages: string[]) => void;
  setSelectedServices: (services: ServiceCategory[]) => void;
  setSelectedCities: (cities: City[]) => void;
}

export default function FilterModal({
  selectedLanguages,
  selectedServices,
  selectedCities,
  className,
  isBlocking = true,
  onClose,
  onSubmit,
  setSelectedLanguages,
  setSelectedServices,
  setSelectedCities,
}: FilterModalProps) {
  const { t } = useTranslation(['jobs', 'common']);
  const {
    options: serviceCategoryOptions,
    selectedOptions: selectedServiceCategories,
    setSelectedOptions: setSelectedServiceCategories,
    setSearchInput: setServiceSearchInput,
  } = useServiceCategorySelector(selectedServices);

  const {
    options: cityOptions,
    selectedOptions: selectedCityOptions,
    setSelectedOptions: setSelectedCityOptions,
  } = useCitySelector(selectedCities);

  const TopDiv = isBlocking ? FullBlockingModal : 'div';

  return (
    <TopDiv className={className}>
      <h3 className="text-secondary mb-4 md:hidden">{t('filterBy', { ns: 'jobs' })}</h3>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:gap-8">
          <FormLanguageSelector
            value={selectedLanguages}
            onChange={setSelectedLanguages}
            optionWrapperClassName="mt-4"
          />

          <div className="md:min-w-[16rem]">
            <InputGroup label={t('form_fields.selectService', { ns: 'common' })}>
              <DropdownMultiTypeahead
                selected={selectedServiceCategories}
                onChange={items => {
                  setSelectedServices(items);
                  setSelectedServiceCategories(items);
                }}
                options={serviceCategoryOptions}
                getOptionLabel={item => item.label}
                getOptionValue={item => item.slug}
                placeholder={t('addService', { ns: 'jobs' })}
                onSearchChange={setServiceSearchInput}
                groupBy={createServiceGroupBy(t)}
                inputMode="select"
              />
            </InputGroup>
          </div>

          <div className="md:min-w-[16rem]">
            <InputGroup label={t('form_fields.city', { ns: 'common' })}>
              <DropdownMultiTypeahead
                selected={selectedCityOptions}
                onChange={items => {
                  setSelectedCities(items);
                  setSelectedCityOptions(items);
                }}
                options={cityOptions}
                getOptionLabel={item => item.name}
                getOptionValue={item => item.id}
                placeholder={t('addCity', { ns: 'jobs' })}
                className="mt-2 md:mt-0"
                inputMode="select"
              />
            </InputGroup>
          </div>
        </div>

        <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
          <Button onClick={onSubmit} className="w-full mt-4 md:w-auto md:mt-0 md:px-8">
            {t('showResults', { ns: 'jobs' })}
          </Button>

          <button
            className="block font-bold mx-auto text-secondary underline leading-tight md:mx-16"
            onClick={onClose}
          >
            {t('common:form_actions.close')}
          </button>
        </div>
      </div>
    </TopDiv>
  );
}
